import { useState, useEffect } from 'react';
import './ParkingSlotManager.css';
import { getApiUrl } from '../utils/api';

/**
 * Gets the ID token from MSAL instance
 * @param {Object} msalInstance - MSAL instance
 * @returns {Promise<string|null>} - ID token or null if unavailable
 */
async function getIdToken(msalInstance) {
  try {
    // Get all accounts and use the first one
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return null;
    }
    const account = accounts[0];
    console.log('Getting ID token for account:', account);
    // Try to get token silently
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: account,
    });
    console.log('ID token response:', response);
    return response.idToken || null;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

function ParkingSlotManager({ user, powerUserMode, msalInstance }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    label: '',
    location: 'basement',
    ev_charging: false,
    default_owner: '',
    available: true,
    display_order: '',
  });

  // Check if user has power user mode enabled
  const canManageSlots = user?.isPowerUser && powerUserMode;

  // Load parking slots from API
  useEffect(() => {
    loadSlots();
  }, []);

  const loadSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error('Failed to get authentication token');
      }

      const response = await fetch(getApiUrl('/api/parkingSlots'), {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load parking slots: ${response.statusText}`);
      }

      const data = await response.json();
      setSlots(data);
    } catch (e) {
      console.error('Failed to load parking slots:', e);
      setError('Failed to load parking slots. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingSlot(null);
    setFormData({
      label: '',
      location: 'basement',
      ev_charging: false,
      default_owner: '',
      available: true,
      display_order: slots.length + 1,
    });
  };

  const handleEdit = (slot) => {
    setEditingSlot(slot);
    setIsCreating(false);
    setFormData({
      label: slot.label,
      location: slot.location || 'basement',
      ev_charging: slot.ev_charging === 1,
      default_owner: slot.default_owner || '',
      available: slot.available === 1,
      display_order: slot.display_order,
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingSlot(null);
    setFormData({
      label: '',
      location: 'basement',
      ev_charging: false,
      default_owner: '',
      available: true,
      display_order: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canManageSlots) {
      alert('You must be a PowerUser with PowerUser mode enabled to manage parking slots.');
      return;
    }

    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error('Failed to get authentication token');
      }

      const payload = {
        ...formData,
      };

      let response;
      if (isCreating) {
        // Create new slot
        response = await fetch(getApiUrl('/api/parkingSlots'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        });
      } else if (editingSlot) {
        // Update existing slot
        payload.id = editingSlot.id;
        response = await fetch(getApiUrl('/api/parkingSlots'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        });
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save parking slot');
      }

      // Reload slots and close form
      await loadSlots();
      handleCancel();
    } catch (e) {
      console.error('Failed to save parking slot:', e);
      alert(`Failed to save parking slot: ${e.message}`);
    }
  };

  const handleDelete = async (slot) => {
    if (!canManageSlots) {
      alert('You must be a PowerUser with PowerUser mode enabled to manage parking slots.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete the parking slot "${slot.label}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error('Failed to get authentication token');
      }

      const response = await fetch(getApiUrl('/api/parkingSlots'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          id: slot.id,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete parking slot');
      }

      // Reload slots
      await loadSlots();
    } catch (e) {
      console.error('Failed to delete parking slot:', e);
      alert(`Failed to delete parking slot: ${e.message}`);
    }
  };

  if (!canManageSlots) {
    return (
      <div className="slot-manager-container">
        <div className="unauthorized-message">
          <h2>⚡ PowerUser Access Required</h2>
          <p>You need to be a PowerUser with PowerUser mode enabled to manage parking slots.</p>
          {user?.isPowerUser && (
            <p className="hint">Enable PowerUser mode using the toggle in the header.</p>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="slot-manager-container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading parking slots...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="slot-manager-container">
        <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="slot-manager-container">
      <div className="slot-manager-header">
        <h2>🚗 Parking Slot Management</h2>
        <button onClick={handleCreateNew} className="btn-create" disabled={isCreating}>
          + Add New Slot
        </button>
      </div>

      {(isCreating || editingSlot) && (
        <div className="slot-manager-modal-overlay" onClick={handleCancel}>
          <div className="slot-manager-modal" onClick={(e) => e.stopPropagation()}>
            <div className="slot-manager-modal-header">
              <h2>{isCreating ? 'Create New Parking Slot' : `Edit Slot: ${editingSlot.label}`}</h2>
              <button className="slot-manager-modal-close" onClick={handleCancel}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="slot-manager-form">
            <div className="form-group">
              <label htmlFor="label">
                Slot Label <span className="required">*</span>
              </label>
              <input
                type="text"
                id="label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g., Slot 1, P-01, Parking Space A"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="location">
                Location <span className="required">*</span>
              </label>
              <select
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              >
                <option value="basement">Basement</option>
                <option value="ground_floor">Ground Floor</option>
              </select>
              <small className="form-hint">Select the physical location of the parking slot</small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.ev_charging}
                  onChange={(e) => setFormData({ ...formData, ev_charging: e.target.checked })}
                />
                <span>⚡ EV Charging Station Available</span>
              </label>
              <small className="form-hint">
                Check if this slot has an electric vehicle charging station
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="default_owner">Default Owner (User ID)</label>
              <input
                type="text"
                id="default_owner"
                value={formData.default_owner}
                onChange={(e) => setFormData({ ...formData, default_owner: e.target.value })}
                placeholder="e.g., user.name@example.com (optional)"
              />
              <small className="form-hint">
                If set, this user will be the default owner of this slot
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="display_order">Display Order</label>
              <input
                type="number"
                id="display_order"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
                }
                placeholder="1, 2, 3..."
                min="1"
              />
              <small className="form-hint">
                Controls the order in which slots appear in the table
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.available}
                  onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                />
                <span>Available for reservations</span>
              </label>
              <small className="form-hint">
                Uncheck to temporarily disable this slot from being reserved
              </small>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-submit">
                {isCreating ? 'Create Slot' : 'Update Slot'}
              </button>
              <button type="button" onClick={handleCancel} className="btn-cancel">
                Cancel
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      <div className="slots-list">
        <h3>Existing Parking Slots ({slots.length})</h3>
        {slots.length === 0 ? (
          <div className="empty-state">
            <p>No parking slots configured yet.</p>
            <p>Click "Add New Slot" to create your first parking slot.</p>
          </div>
        ) : (
          <div className="slots-table-wrapper">
            <table className="slots-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Label</th>
                  <th>Location</th>
                  <th>EV Charging</th>
                  <th>Default Owner</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {slots.map((slot) => (
                  <tr key={slot.id}>
                    <td>{slot.display_order}</td>
                    <td>
                      <strong>{slot.label}</strong>
                    </td>
                    <td>
                      <span className="location-badge">
                        {slot.location === 'basement' ? '🏢 Basement' : '🏛️ Ground Floor'}
                      </span>
                    </td>
                    <td className="text-center">{slot.ev_charging ? '⚡' : '-'}</td>
                    <td>{slot.default_owner || <em className="text-muted">None</em>}</td>
                    <td>
                      <span
                        className={`status-badge ${slot.available ? 'available' : 'unavailable'}`}
                      >
                        {slot.available ? '✓ Available' : '✗ Unavailable'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(slot)}
                          className="btn-edit"
                          disabled={editingSlot?.id === slot.id}
                        >
                          Edit
                        </button>
                        <button onClick={() => handleDelete(slot)} className="btn-delete">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParkingSlotManager;
